<script setup>
import { computed, onMounted, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import commercialWidgetsAPI from 'dashboard/api/commercialWidgets';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import { useCommercialWidgets } from './useCommercialWidgets';
import WidgetBasicFields from './WidgetBasicFields.vue';
import WidgetConditions from './WidgetConditions.vue';
import WidgetRateFields from './WidgetRateFields.vue';
import WidgetGroupFields from './WidgetGroupFields.vue';
import {
  GROUPED_VISUALIZATIONS,
  allowedMeasureTypes,
  buildPayload,
  defaultForm,
  formFromWidget,
  isFormComplete,
  numericFields,
  fieldRef,
  visibleEntities,
} from './widgetConfig';

const props = defineProps({
  widget: { type: Object, default: null },
  widgets: { type: Array, default: () => [] },
  position: { type: Number, default: undefined },
});

const emit = defineEmits(['saved', 'close']);

const { t } = useI18n();
const { catalog, fetchCatalog } = useCommercialWidgets();

const dialogRef = ref(null);
const isLoadingCatalog = ref(true);
const hasCatalogError = ref(false);
const isSaving = ref(false);
const serverErrors = ref([]);

const form = reactive(
  props.widget ? formFromWidget(props.widget) : defaultForm()
);

const isEdit = computed(() => !!props.widget);
const isRate = computed(() => form.visualization === 'rate');
const isGrouped = computed(() =>
  GROUPED_VISUALIZATIONS.includes(form.visualization)
);
const canSubmit = computed(
  () => !!catalog.value && isFormComplete(form, catalog.value)
);

const loadCatalog = async () => {
  isLoadingCatalog.value = true;
  hasCatalogError.value = false;
  try {
    await fetchCatalog();
  } catch {
    hasCatalogError.value = true;
  } finally {
    isLoadingCatalog.value = false;
  }
};

onMounted(() => {
  dialogRef.value.open();
  loadCatalog();
});

watch(
  () => form.visualization,
  visualization => {
    if (!allowedMeasureTypes(visualization).includes(form.measureType)) {
      form.measureType = 'count';
    }
  }
);

watch(
  () => form.measureType,
  measureType => {
    if (measureType === 'count') form.measureField = '';
  }
);

watch(
  () => form.unit,
  unit => {
    const isNumericField = numericFields(catalog.value, unit).some(
      field => fieldRef(field) === form.measureField
    );
    if (!isNumericField) form.measureField = '';
    form.groupBy = '';
    if (unit !== 'deal' && form.dateBasis === 'closing') {
      form.dateBasis = 'auto';
    }
    form.conditions = form.conditions.filter(condition =>
      visibleEntities(unit).includes(condition.entity)
    );
  }
);

const submit = async () => {
  if (!canSubmit.value || isSaving.value) return;
  isSaving.value = true;
  serverErrors.value = [];
  try {
    const payload = buildPayload(form);
    if (isEdit.value) {
      await commercialWidgetsAPI.update(props.widget.id, payload);
    } else {
      await commercialWidgetsAPI.create({
        ...payload,
        position: props.position,
      });
    }
    useAlert(
      t(
        isEdit.value
          ? 'COMMERCIAL_WIDGETS_DIALOG.SUCCESS.UPDATED'
          : 'COMMERCIAL_WIDGETS_DIALOG.SUCCESS.CREATED'
      )
    );
    emit('saved');
    dialogRef.value?.close();
  } catch (error) {
    const data = error.response?.data;
    if (data?.error === 'in_use_by_rates') {
      serverErrors.value = [
        t('COMMERCIAL_WIDGETS_DIALOG.ERRORS.IN_USE_BY_RATES', {
          rates: data.rates.map(rate => rate.title).join(', '),
        }),
      ];
    } else {
      serverErrors.value = data?.errors || [
        t('COMMERCIAL_WIDGETS_DIALOG.ERRORS.GENERIC'),
      ];
    }
    useAlert(serverErrors.value[0]);
  } finally {
    isSaving.value = false;
  }
};
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="xl"
    overflow-y-auto
    :title="
      t(
        isEdit
          ? 'COMMERCIAL_WIDGETS_DIALOG.TITLE_EDIT'
          : 'COMMERCIAL_WIDGETS_DIALOG.TITLE_CREATE'
      )
    "
    :confirm-button-label="
      t(
        isEdit
          ? 'COMMERCIAL_WIDGETS_DIALOG.BUTTONS.SAVE'
          : 'COMMERCIAL_WIDGETS_DIALOG.BUTTONS.CREATE'
      )
    "
    :disable-confirm-button="!canSubmit"
    :is-loading="isSaving"
    @confirm="submit"
    @close="emit('close')"
  >
    <div
      v-if="isLoadingCatalog"
      class="flex items-center justify-center gap-2 py-8 text-sm text-n-slate-11"
    >
      <Spinner />
      {{ t('COMMERCIAL_WIDGETS_DIALOG.LOADING') }}
    </div>
    <div
      v-else-if="hasCatalogError"
      class="flex flex-col items-start gap-2 py-4 text-sm text-n-ruby-9"
    >
      {{ t('COMMERCIAL_WIDGETS_DIALOG.ERRORS.CATALOG') }}
      <button
        type="button"
        class="font-medium underline text-n-slate-12"
        @click="loadCatalog"
      >
        {{ t('COMMERCIAL_WIDGETS_DIALOG.ERRORS.RETRY') }}
      </button>
    </div>
    <div v-else class="flex flex-col gap-4">
      <WidgetBasicFields
        v-model:title="form.title"
        v-model:visualization="form.visualization"
        v-model:unit="form.unit"
        v-model:measure-type="form.measureType"
        v-model:measure-field="form.measureField"
        v-model:date-basis="form.dateBasis"
        :catalog="catalog"
      >
        <WidgetGroupFields
          v-if="isGrouped"
          v-model="form.groupBy"
          :unit="form.unit"
          :catalog="catalog"
        />
        <WidgetConditions
          v-model="form.conditions"
          :unit="form.unit"
          :catalog="catalog"
        />
      </WidgetBasicFields>
      <WidgetRateFields
        v-if="isRate"
        v-model:numerator-id="form.numeratorId"
        v-model:denominator-id="form.denominatorId"
        :widgets="widgets"
        :current-id="widget?.id"
      />
      <ul
        v-if="serverErrors.length"
        class="flex flex-col gap-1 p-0 m-0 text-sm list-none text-n-ruby-9"
      >
        <li v-for="message in serverErrors" :key="message">{{ message }}</li>
      </ul>
    </div>
  </Dialog>
</template>
